/**
 * Merlin watches — "tell Merlin what to watch, get a short dated readout back"
 * (kanban 8e4296bc, AI/Code check-in 2026-10-12).
 *
 * Backend: app/routes/merlin_watches.py. The readout HTML itself is served by
 * the shared reports endpoint (signalsReportsApi.fetchHtml).
 */
import api from './api';
import type { SignalReport } from './signalsReports';

export type WatchCadence = 'daily' | 'every_48h' | 'weekly' | 'monthly';
export type WatchStatus = 'active' | 'paused' | 'removed';
export type RunStatus = 'queued' | 'running' | 'published' | 'failed';
export type RunTrigger = 'created' | 'scheduled' | 'manual' | 'refinement';
/** `briefing`: the reader's daily briefing (the former personal digest,
 * kanban 001b6e2b) — one per reader, same lifecycle as any watch. */
export type WatchKind = 'watch' | 'briefing';

export interface WatchRun {
  id: string;
  status: RunStatus;
  trigger: RunTrigger;
  refinement: string | null;
  queued_at: string;
  started_at: string | null;
  finished_at: string | null;
  report_id: string | null;
  audit_conversation_id: string | null;
  model: string | null;
  error: string | null;
}

export interface WatchEvent {
  id: string;
  kind: string;
  actor: string;
  payload: Record<string, unknown>;
  run_id: string | null;
  created_at: string;
}

export interface Watch {
  id: string;
  owner_user_id: string;
  owner_name: string | null;
  subject: string;
  kind: WatchKind;
  cadence: WatchCadence;
  cadence_label: string;
  status: WatchStatus;
  next_run_at: string | null;
  last_run_at: string | null;
  run_count: number;
  created_at: string;
  updated_at: string;
  /** The queued / running run, when Merlin is checking right now. */
  open_run: WatchRun | null;
  /** The newest published readout (metadata; HTML via signalsReportsApi). */
  latest: SignalReport | null;
  /** The watch in one line: the newest readout's headline, else the first
   * clause of the subject. The tab's name (kanban f967fdaf). */
  headline: string;
  /** What the watch looks at, as the newest readout said it (empty until a
   * readout has named it). */
  looks_at: string[];
  /** The newest readout's at-a-glance lines (one number each; empty until a
   * readout carries them). */
  at_a_glance: string[];
}

export interface WatchDetail {
  watch: Watch;
  runs: WatchRun[];
  events: WatchEvent[];
  refinements: string[];
}

export interface RunQueued {
  status: string;
  detail: string;
  run: WatchRun;
}

const BASE = '/watches';

export const merlinWatchesApi = {
  list: async (scope: 'mine' | 'all' = 'mine'): Promise<Watch[]> =>
    (await api.get<{ items: Watch[] }>(BASE, { params: { scope } })).data.items,
  create: async (
    subject: string,
    cadence: WatchCadence,
    kind: WatchKind = 'watch',
  ): Promise<WatchDetail> => (await api.post<WatchDetail>(BASE, { subject, cadence, kind })).data,
  get: async (id: string): Promise<WatchDetail> => (await api.get<WatchDetail>(`${BASE}/${id}`)).data,
  setCadence: async (id: string, cadence: WatchCadence): Promise<Watch> =>
    (await api.patch<Watch>(`${BASE}/${id}`, { cadence })).data,
  refine: async (id: string, text: string): Promise<WatchDetail> =>
    (await api.post<WatchDetail>(`${BASE}/${id}/refine`, { text })).data,
  runNow: async (id: string): Promise<RunQueued> => (await api.post<RunQueued>(`${BASE}/${id}/run`)).data,
  pause: async (id: string): Promise<Watch> => (await api.post<Watch>(`${BASE}/${id}/pause`)).data,
  resume: async (id: string): Promise<Watch> => (await api.post<Watch>(`${BASE}/${id}/resume`)).data,
  remove: async (id: string): Promise<void> => {
    await api.delete(`${BASE}/${id}`);
  },
};
